import { useState, useMemo, useEffect } from "react";
import { 
  Search, Package, ArrowLeft, Plus, Edit, Copy, Archive, TrendingUp,
  MoreHorizontal, Download, Filter, CheckCircle2, XCircle, Clock, BarChart3,
  AlertTriangle, ToggleLeft, ToggleRight
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Order } from "./OrderDetailModal";
import { formatUKDateTime } from "./OrderDetailModal";

interface Product {
  id: string;
  name: string;
  description: string | null;
  price: number;
  currency: string;
  sku: string | null;
  status: "active" | "draft" | "archived";
  stock_quantity: number | null;
  image_url: string | null;
  category: string | null;
  created_at: string;
  updated_at: string;
}

interface DerivedProduct {
  name: string;
  unitsSold: number;
  revenue: number;
  avgPrice: number;
  lastSold: string | null;
  firstSold: string | null;
  orderCount: number;
  dbProduct?: Product;
}

interface ProductsPageProps {
  orders: Order[];
  onViewOrder: (order: Order) => void;
}

const statusConfig = {
  active: { label: "Active", icon: CheckCircle2, color: "bg-emerald-900/30 text-emerald-400 border-emerald-900/50" },
  draft: { label: "Draft", icon: Clock, color: "bg-amber-900/30 text-amber-400 border-amber-900/50" },
  archived: { label: "Archived", icon: XCircle, color: "bg-slate-700/30 text-slate-400 border-slate-600/50" },
};

export function ProductsPage({ orders, onViewOrder }: ProductsPageProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<"revenue" | "units" | "lastSold" | "name">("revenue");
  const [sortDesc, setSortDesc] = useState(true);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<DerivedProduct | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editingProduct, setEditingProduct] = useState<Partial<Product>>({});
  const [inventoryEnabled, setInventoryEnabled] = useState(false);

  // Fetch products from database
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setProducts((data as Product[]) || []);
    } catch (err) {
      console.error("Error fetching products:", err);
      toast.error("Failed to load products");
    } finally {
      setIsLoading(false);
    }
  };

  // Derive product performance from orders
  const derivedProducts = useMemo(() => {
    const productMap = new Map<string, DerivedProduct>();

    orders.forEach(order => {
      order.items.forEach(item => {
        const key = item.name.toLowerCase().trim();
        const existing = productMap.get(key);
        
        if (existing) {
          existing.unitsSold += item.quantity;
          existing.revenue += item.price * item.quantity;
          existing.orderCount += 1;
          if (!existing.lastSold || new Date(order.created_at) > new Date(existing.lastSold)) {
            existing.lastSold = order.created_at;
          }
          if (!existing.firstSold || new Date(order.created_at) < new Date(existing.firstSold)) {
            existing.firstSold = order.created_at;
          }
        } else {
          productMap.set(key, {
            name: item.name,
            unitsSold: item.quantity,
            revenue: item.price * item.quantity,
            avgPrice: item.price,
            lastSold: order.created_at,
            firstSold: order.created_at,
            orderCount: 1,
          });
        }
      });
    });

    // Calculate avg price and match with DB products
    const derived: DerivedProduct[] = [];
    productMap.forEach((product, key) => {
      product.avgPrice = product.revenue / product.unitsSold;
      // Match with DB product by name (case-insensitive)
      product.dbProduct = products.find(p => p.name.toLowerCase().trim() === key);
      derived.push(product);
    });

    return derived;
  }, [orders, products]);

  // Top 5 products by revenue and units
  const topByRevenue = useMemo(() => {
    return [...derivedProducts].sort((a, b) => b.revenue - a.revenue).slice(0, 5);
  }, [derivedProducts]);

  const topByUnits = useMemo(() => {
    return [...derivedProducts].sort((a, b) => b.unitsSold - a.unitsSold).slice(0, 5);
  }, [derivedProducts]);

  // Sorted and filtered products
  const sortedProducts = useMemo(() => {
    let result = [...derivedProducts];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p => p.name.toLowerCase().includes(query));
    }

    result.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case "revenue":
          comparison = a.revenue - b.revenue;
          break;
        case "units":
          comparison = a.unitsSold - b.unitsSold;
          break;
        case "lastSold":
          comparison = new Date(a.lastSold || 0).getTime() - new Date(b.lastSold || 0).getTime();
          break;
        case "name":
          comparison = a.name.localeCompare(b.name);
          break;
      }
      return sortDesc ? -comparison : comparison;
    });

    return result;
  }, [derivedProducts, searchQuery, sortBy, sortDesc]);

  // Total stats
  const totalStats = useMemo(() => {
    return derivedProducts.reduce(
      (acc, p) => ({
        revenue: acc.revenue + p.revenue,
        units: acc.units + p.unitsSold,
        products: acc.products + 1,
      }),
      { revenue: 0, units: 0, products: 0 }
    );
  }, [derivedProducts]);

  // Save product to DB
  const handleSaveProduct = async () => {
    if (!editingProduct.name) {
      toast.error("Product name is required");
      return;
    }

    try {
      if (editingProduct.id) {
        const { error } = await supabase
          .from("products")
          .update({
            name: editingProduct.name,
            description: editingProduct.description,
            price: editingProduct.price || 0,
            sku: editingProduct.sku,
            status: editingProduct.status || "active",
            stock_quantity: inventoryEnabled ? editingProduct.stock_quantity : null,
            image_url: editingProduct.image_url,
            category: editingProduct.category,
          })
          .eq("id", editingProduct.id);

        if (error) throw error;
        toast.success("Product updated");
      } else {
        const { error } = await supabase
          .from("products")
          .insert({
            name: editingProduct.name,
            description: editingProduct.description,
            price: editingProduct.price || 0,
            sku: editingProduct.sku,
            status: editingProduct.status || "active",
            stock_quantity: inventoryEnabled ? editingProduct.stock_quantity : null,
            image_url: editingProduct.image_url,
            category: editingProduct.category,
          });

        if (error) throw error;
        toast.success("Product created");
      }

      setIsEditorOpen(false);
      setEditingProduct({});
      fetchProducts();
    } catch (err) {
      console.error("Error saving product:", err);
      toast.error("Failed to save product");
    }
  };

  // Export CSV
  const handleExportCSV = () => {
    const headers = ["Product Name", "Units Sold", "Revenue", "Avg Price", "Orders", "Last Sold"];
    const rows = sortedProducts.map(p => [
      p.name,
      p.unitsSold.toString(),
      `£${p.revenue.toFixed(2)}`,
      `£${p.avgPrice.toFixed(2)}`,
      p.orderCount.toString(),
      p.lastSold ? formatUKDateTime(p.lastSold) : "Never",
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `product-performance-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Products exported");
  };

  // Product detail view
  if (selectedProduct) {
    const relatedOrders = orders.filter(o => 
      o.items.some(i => i.name.toLowerCase().trim() === selectedProduct.name.toLowerCase().trim())
    ).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    return (
      <div className="space-y-6">
        <Button
          variant="ghost"
          onClick={() => setSelectedProduct(null)}
          className="text-slate-400 hover:text-slate-100 -ml-2"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Products
        </Button>

        <Card className="bg-slate-800/50 border-slate-700 p-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-emerald-900/30 rounded-xl flex items-center justify-center">
              <Package className="w-8 h-8 text-emerald-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-semibold text-slate-100">
                {selectedProduct.name}
              </h2>
              <p className="text-slate-400 text-sm mt-1">
                First sold: {selectedProduct.firstSold ? formatUKDateTime(selectedProduct.firstSold) : "N/A"}
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-emerald-400">
                £{selectedProduct.revenue.toFixed(2)}
              </div>
              <div className="text-slate-400 text-sm">Total Revenue</div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
            <div className="bg-slate-900/50 rounded-lg p-4">
              <div className="text-2xl font-bold text-slate-100">{selectedProduct.unitsSold}</div>
              <div className="text-slate-500 text-sm">Units Sold</div>
            </div>
            <div className="bg-slate-900/50 rounded-lg p-4">
              <div className="text-2xl font-bold text-slate-100">£{selectedProduct.avgPrice.toFixed(2)}</div>
              <div className="text-slate-500 text-sm">Avg Price</div>
            </div>
            <div className="bg-slate-900/50 rounded-lg p-4">
              <div className="text-2xl font-bold text-slate-100">{selectedProduct.orderCount}</div>
              <div className="text-slate-500 text-sm">Orders</div>
            </div>
            <div className="bg-slate-900/50 rounded-lg p-4">
              <div className="text-lg font-bold text-slate-100">
                {selectedProduct.lastSold ? formatUKDateTime(selectedProduct.lastSold) : "Never"}
              </div>
              <div className="text-slate-500 text-sm">Last Sold</div>
            </div>
          </div>

          {selectedProduct.dbProduct && (
            <div className="mt-4 flex gap-2">
              <Button
                onClick={() => {
                  setEditingProduct(selectedProduct.dbProduct!);
                  setIsEditorOpen(true);
                }}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Product Details
              </Button>
            </div>
          )}
        </Card>

        {/* Recent Orders */}
        <div>
          <h3 className="text-lg font-semibold text-slate-100 mb-3">Recent Orders</h3>
          {relatedOrders.length === 0 ? (
            <Card className="bg-slate-900/70 border-slate-700/60 p-8 text-center">
              <p className="text-slate-400">No orders found for this product</p>
            </Card>
          ) : (
            <Card className="bg-slate-900/70 border-slate-700/60 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700/60">
                    <TableHead className="text-slate-300 text-xs">Order</TableHead>
                    <TableHead className="text-slate-300 text-xs">Date</TableHead>
                    <TableHead className="text-slate-300 text-xs">Customer</TableHead>
                    <TableHead className="text-slate-300 text-xs">Qty</TableHead>
                    <TableHead className="text-slate-300 text-xs">Total</TableHead>
                    <TableHead className="text-slate-300 text-xs"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {relatedOrders.slice(0, 10).map(order => {
                    const item = order.items.find(i => i.name.toLowerCase().trim() === selectedProduct.name.toLowerCase().trim());
                    return (
                      <TableRow key={order.id} className="border-slate-700/60 hover:bg-slate-800/50">
                        <TableCell className="font-mono text-xs text-slate-200">{order.order_number}</TableCell>
                        <TableCell className="text-slate-300 text-xs">{formatUKDateTime(order.created_at)}</TableCell>
                        <TableCell className="text-slate-300 text-sm">{order.customer_name}</TableCell>
                        <TableCell className="text-slate-100 font-semibold">{item?.quantity || 0}</TableCell>
                        <TableCell className="text-emerald-400 font-semibold">
                          £{((item?.price || 0) * (item?.quantity || 0)).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onViewOrder(order)}
                            className="text-emerald-400 hover:text-emerald-300 h-7 text-xs"
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // Empty state - but check if there are orders first
  if (!isLoading && derivedProducts.length === 0 && orders.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-100">Product Performance</h2>
            <p className="text-slate-400 text-sm mt-0.5">Track sales and inventory</p>
          </div>
        </div>
        <Card className="bg-slate-900/70 border-slate-700/60 p-12 text-center">
          <div className="w-20 h-20 bg-emerald-900/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Package className="w-10 h-10 text-emerald-400" />
          </div>
          <h3 className="text-xl font-semibold text-slate-100 mb-2">No products yet</h3>
          <p className="text-slate-400 mb-6 max-w-md mx-auto">
            Product performance data will appear automatically once you receive orders. Each order line item creates a product record.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-100">
            Product Performance
            {!inventoryEnabled && (
              <span className="text-slate-500 text-sm font-normal ml-2">(Inventory optional)</span>
            )}
          </h2>
          <p className="text-slate-400 text-sm mt-0.5">
            {derivedProducts.length} products • £{totalStats.revenue.toFixed(2)} total revenue • {totalStats.units} units sold
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 bg-slate-800/50 px-3 py-1.5 rounded-lg border border-slate-700">
            <span className="text-slate-400 text-xs">Inventory</span>
            <Switch
              checked={inventoryEnabled}
              onCheckedChange={setInventoryEnabled}
              className="data-[state=checked]:bg-emerald-600"
            />
          </div>
        </div>
      </div>

      {/* Top Products Cards */}
      {derivedProducts.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="bg-slate-800/50 border-slate-700 p-4">
            <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
              Top 5 by Revenue
            </h3>
            <div className="space-y-2">
              {topByRevenue.map((product, idx) => (
                <div 
                  key={product.name} 
                  className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-slate-700/50 cursor-pointer"
                  onClick={() => setSelectedProduct(product)}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 text-xs font-mono w-4">{idx + 1}.</span>
                    <span className="text-slate-200 text-sm truncate max-w-[180px]">{product.name}</span>
                  </div>
                  <span className="text-emerald-400 font-semibold text-sm">£{product.revenue.toFixed(0)}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700 p-4">
            <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-blue-400" />
              Top 5 by Units Sold
            </h3>
            <div className="space-y-2">
              {topByUnits.map((product, idx) => (
                <div 
                  key={product.name} 
                  className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-slate-700/50 cursor-pointer"
                  onClick={() => setSelectedProduct(product)}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 text-xs font-mono w-4">{idx + 1}.</span>
                    <span className="text-slate-200 text-sm truncate max-w-[180px]">{product.name}</span>
                  </div>
                  <span className="text-blue-400 font-semibold text-sm">{product.unitsSold} units</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-900/70 border-slate-700 text-slate-100 placeholder:text-slate-500 h-9"
          />
        </div>
        
        <div className="flex gap-2 flex-wrap">
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
            <SelectTrigger className="w-[130px] bg-slate-900/70 border-slate-700 text-slate-300 h-9">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="revenue">Revenue</SelectItem>
              <SelectItem value="units">Units Sold</SelectItem>
              <SelectItem value="lastSold">Last Sold</SelectItem>
              <SelectItem value="name">Name</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setSortDesc(!sortDesc)}
            className="border-slate-700 text-slate-300 h-9"
          >
            {sortDesc ? "↓ Desc" : "↑ Asc"}
          </Button>

          <Button
            onClick={handleExportCSV}
            variant="outline"
            size="sm"
            className="border-slate-700 text-slate-300 h-9"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>

          <Button
            onClick={() => {
              setEditingProduct({ status: "active" });
              setIsEditorOpen(true);
            }}
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 h-9"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Product
          </Button>
        </div>
      </div>

      {/* Products Table */}
      {isLoading ? (
        <Card className="bg-slate-900/70 border-slate-700/60 p-12 text-center">
          <p className="text-slate-400">Loading products...</p>
        </Card>
      ) : sortedProducts.length === 0 ? (
        <Card className="bg-slate-900/70 border-slate-700/60 p-12 text-center">
          <Filter className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">No products match your search</p>
        </Card>
      ) : (
        <Card className="bg-slate-900/70 border-slate-700/60 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700/60">
                  <TableHead className="text-slate-300 text-xs">Product</TableHead>
                  <TableHead className="text-slate-300 text-xs text-right">Units Sold</TableHead>
                  <TableHead className="text-slate-300 text-xs text-right">Revenue</TableHead>
                  <TableHead className="text-slate-300 text-xs text-right hidden sm:table-cell">Avg Price</TableHead>
                  <TableHead className="text-slate-300 text-xs hidden md:table-cell">Last Sold</TableHead>
                  {inventoryEnabled && (
                    <TableHead className="text-slate-300 text-xs text-right">Stock</TableHead>
                  )}
                  <TableHead className="text-slate-300 text-xs w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedProducts.map((product) => (
                  <TableRow
                    key={product.name}
                    className="border-slate-700/60 hover:bg-slate-800/50 cursor-pointer"
                    onClick={() => setSelectedProduct(product)}
                  >
                    <TableCell className="py-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-900/30 rounded-lg flex items-center justify-center">
                          <Package className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div>
                          <div className="text-slate-100 font-medium text-sm max-w-[200px] truncate">
                            {product.name}
                          </div>
                          <div className="text-slate-500 text-xs">{product.orderCount} orders</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-100 font-semibold text-sm text-right">
                      {product.unitsSold}
                    </TableCell>
                    <TableCell className="text-emerald-400 font-semibold text-sm text-right">
                      £{product.revenue.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-slate-400 text-sm text-right hidden sm:table-cell">
                      £{product.avgPrice.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-slate-400 text-xs hidden md:table-cell">
                      {product.lastSold ? formatUKDateTime(product.lastSold) : "Never"}
                    </TableCell>
                    {inventoryEnabled && (
                      <TableCell className="text-right">
                        {product.dbProduct?.stock_quantity !== null && product.dbProduct?.stock_quantity !== undefined ? (
                          <Badge className={
                            product.dbProduct.stock_quantity <= 5 
                              ? "bg-red-900/30 text-red-400 border-red-900/50"
                              : "bg-slate-700 text-slate-300"
                          }>
                            {product.dbProduct.stock_quantity}
                          </Badge>
                        ) : (
                          <span className="text-slate-500 text-xs">—</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" className="text-slate-400 h-7">
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Product Editor Dialog */}
      <Dialog open={isEditorOpen} onOpenChange={setIsEditorOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-100">
              {editingProduct.id ? "Edit Product" : "Add New Product"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300">Product Name *</Label>
              <Input
                value={editingProduct.name || ""}
                onChange={(e) => setEditingProduct(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Sensory Pad - Blue"
                className="bg-slate-800 border-slate-700 text-slate-100 mt-1"
              />
            </div>

            <div>
              <Label className="text-slate-300">Description</Label>
              <Textarea
                value={editingProduct.description || ""}
                onChange={(e) => setEditingProduct(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Product description..."
                className="bg-slate-800 border-slate-700 text-slate-100 mt-1"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Price (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editingProduct.price || ""}
                  onChange={(e) => setEditingProduct(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                  placeholder="0.00"
                  className="bg-slate-800 border-slate-700 text-slate-100 mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">SKU</Label>
                <Input
                  value={editingProduct.sku || ""}
                  onChange={(e) => setEditingProduct(prev => ({ ...prev, sku: e.target.value }))}
                  placeholder="e.g., SP-BLU-001"
                  className="bg-slate-800 border-slate-700 text-slate-100 mt-1"
                />
              </div>
            </div>

            {inventoryEnabled && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Stock Quantity</Label>
                  <Input
                    type="number"
                    value={editingProduct.stock_quantity ?? ""}
                    onChange={(e) => setEditingProduct(prev => ({ ...prev, stock_quantity: e.target.value ? parseInt(e.target.value) : null }))}
                    placeholder="0"
                    className="bg-slate-800 border-slate-700 text-slate-100 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Category</Label>
                  <Input
                    value={editingProduct.category || ""}
                    onChange={(e) => setEditingProduct(prev => ({ ...prev, category: e.target.value }))}
                    placeholder="e.g., Sensory Pads"
                    className="bg-slate-800 border-slate-700 text-slate-100 mt-1"
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditorOpen(false);
                  setEditingProduct({});
                }}
                className="border-slate-600 text-slate-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveProduct}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {editingProduct.id ? "Save Changes" : "Create Product"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
